"use client"

import * as React from "react"
import { Popover as PopoverPrimitive } from "radix-ui"
import { CheckIcon, ChevronDownIcon, SearchIcon, XIcon } from "lucide-react"

import { cn } from "../lib/cn"
import { Button } from "./button"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "./input-group"

type ComboboxValue<TItem> = TItem | TItem[] | null

type ComboboxProps<TItem> = Omit<
  React.ComponentProps<typeof PopoverPrimitive.Root>,
  "defaultValue" | "onValueChange"
> & {
  autoHighlight?: boolean
  defaultValue?: ComboboxValue<TItem>
  isItemEqualToValue?: (item: TItem, value: TItem) => boolean
  itemToStringLabel?: (item: TItem) => string
  itemToStringValue?: (item: TItem) => string
  items?: TItem[]
  multiple?: boolean
  onInputValueChange?: (value: string) => void
  onValueChange?: (value: TItem[] & TItem) => void
  value?: ComboboxValue<TItem>
}

type ComboboxContextValue = {
  filteredItems: unknown[]
  getItemLabel: (item: unknown) => string
  inputValue: string
  isItemSelected: (item: unknown) => boolean
  multiple: boolean
  open: boolean
  selectedValue: ComboboxValue<unknown>
  selectItem: (item: unknown) => void
  setInputValue: (value: string) => void
  setOpen: (open: boolean) => void
}

const ComboboxContext = React.createContext<ComboboxContextValue | null>(null)

function useComboboxContext() {
  const context = React.useContext(ComboboxContext)

  if (!context) {
    throw new Error("Combobox components must be used within Combobox")
  }

  return context
}

function defaultItemToString(item: unknown) {
  if (item == null) {
    return ""
  }

  if (typeof item === "string") {
    return item
  }

  if (typeof item === "number") {
    return String(item)
  }

  if (typeof item === "object" && "label" in item) {
    return String((item as { label?: unknown }).label ?? "")
  }

  return String(item)
}

function Combobox<TItem>({
  children,
  defaultValue = null,
  isItemEqualToValue,
  itemToStringLabel = defaultItemToString,
  items = [],
  multiple = false,
  onInputValueChange,
  onValueChange,
  open,
  value,
  onOpenChange,
  ...props
}: ComboboxProps<TItem>) {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const [internalValue, setInternalValue] =
    React.useState<ComboboxValue<TItem>>(defaultValue)
  const selectedValue = value !== undefined ? value : internalValue
  const [inputValue, setInputValueState] = React.useState(() => {
    if (multiple || Array.isArray(selectedValue) || selectedValue == null) {
      return ""
    }

    return itemToStringLabel(selectedValue)
  })

  const currentOpen = open ?? internalOpen
  const setOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (
        !nextOpen &&
        !multiple &&
        !Array.isArray(selectedValue) &&
        selectedValue != null
      ) {
        setInputValueState(itemToStringLabel(selectedValue))
      }

      setInternalOpen(nextOpen)
      onOpenChange?.(nextOpen)
    },
    [itemToStringLabel, multiple, onOpenChange, selectedValue],
  )

  const setInputValue = React.useCallback(
    (nextValue: string) => {
      setInputValueState(nextValue)
      onInputValueChange?.(nextValue)
    },
    [onInputValueChange],
  )

  React.useEffect(() => {
    if (multiple || Array.isArray(selectedValue) || selectedValue == null) {
      return
    }

    setInputValueState(itemToStringLabel(selectedValue))
  }, [itemToStringLabel, multiple, selectedValue])

  const getItemLabel = React.useCallback(
    (item: unknown) => itemToStringLabel(item as TItem),
    [itemToStringLabel],
  )

  const isSameItem = React.useCallback(
    (item: unknown, nextValue: unknown) => {
      if (isItemEqualToValue) {
        return isItemEqualToValue(item as TItem, nextValue as TItem)
      }

      return item === nextValue
    },
    [isItemEqualToValue],
  )

  const isItemSelected = React.useCallback(
    (item: unknown) => {
      if (Array.isArray(selectedValue)) {
        return selectedValue.some((nextValue) => isSameItem(item, nextValue))
      }

      return selectedValue != null && isSameItem(item, selectedValue)
    },
    [isSameItem, selectedValue],
  )

  const selectItem = React.useCallback(
    (item: unknown) => {
      if (multiple) {
        const selectedItems = Array.isArray(selectedValue)
          ? selectedValue
          : []
        const exists = selectedItems.some((nextValue) =>
          isSameItem(item, nextValue),
        )
        const nextValue = exists
          ? selectedItems.filter((nextValue) => !isSameItem(item, nextValue))
          : [...selectedItems, item as TItem]

        setInternalValue(nextValue)
        onValueChange?.(nextValue as TItem[] & TItem)
        return
      }

      setInternalValue(item as TItem)
      setInputValueState(itemToStringLabel(item as TItem))
      onValueChange?.(item as TItem[] & TItem)
      setOpen(false)
    },
    [
      isSameItem,
      itemToStringLabel,
      multiple,
      onValueChange,
      selectedValue,
      setOpen,
    ],
  )

  const filteredItems = React.useMemo(() => {
    const keyword = inputValue.trim().toLowerCase()

    if (!keyword) {
      return items
    }

    return items.filter((item) =>
      itemToStringLabel(item).toLowerCase().includes(keyword),
    )
  }, [inputValue, itemToStringLabel, items])

  const contextValue = React.useMemo<ComboboxContextValue>(
    () => ({
      filteredItems,
      getItemLabel,
      inputValue,
      isItemSelected,
      multiple,
      open: currentOpen,
      selectedValue: selectedValue as ComboboxValue<unknown>,
      selectItem,
      setInputValue,
      setOpen,
    }),
    [
      currentOpen,
      filteredItems,
      getItemLabel,
      inputValue,
      isItemSelected,
      multiple,
      selectItem,
      selectedValue,
      setInputValue,
      setOpen,
    ],
  )

  return (
    <ComboboxContext.Provider value={contextValue}>
      <PopoverPrimitive.Root open={currentOpen} onOpenChange={setOpen} {...props}>
        {children}
      </PopoverPrimitive.Root>
    </ComboboxContext.Provider>
  )
}

function ComboboxValue() {
  const { getItemLabel, selectedValue } = useComboboxContext()

  if (Array.isArray(selectedValue)) {
    return selectedValue.map(getItemLabel).join(", ")
  }

  return selectedValue == null ? null : getItemLabel(selectedValue)
}

function ComboboxTrigger({
  className,
  children,
  onClick,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  const { setInputValue } = useComboboxContext()

  return (
    <PopoverPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cn("[&_svg:not([class*='size-'])]:size-4", className)}
      onClick={(event) => {
        setInputValue("")
        onClick?.(event)
      }}
      {...props}
    >
      {children}
      <ChevronDownIcon
        data-slot="combobox-trigger-icon"
        className="pointer-events-none size-4 text-muted-foreground"
      />
    </PopoverPrimitive.Trigger>
  )
}

function ComboboxClear({
  className,
  ...props
}: React.ComponentProps<typeof InputGroupButton>) {
  const { setInputValue } = useComboboxContext()

  return (
    <InputGroupButton
      data-slot="combobox-clear"
      variant="ghost"
      size="icon-xs"
      className={cn(className)}
      onClick={() => setInputValue("")}
      {...props}
    >
      <XIcon className="pointer-events-none" />
    </InputGroupButton>
  )
}

function ComboboxInput({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  ...props
}: React.ComponentProps<"input"> & {
  showTrigger?: boolean
  showClear?: boolean
}) {
  const { inputValue, open, setInputValue, setOpen } = useComboboxContext()

  return (
    <PopoverPrimitive.Anchor asChild>
      <InputGroup className={cn("w-auto", className)}>
        <InputGroupInput
          disabled={disabled}
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value)
            props.onChange?.(event)
          }}
          onFocus={(event) => {
            setOpen(true)
            props.onFocus?.(event)
          }}
          {...props}
        />
        <InputGroupAddon align="inline-end">
          {showTrigger && (
            <InputGroupButton
              size="icon-xs"
              variant="ghost"
              asChild
              data-slot="input-group-button"
              className="group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent"
              disabled={disabled}
            >
              <ComboboxTrigger
                aria-expanded={open}
                aria-label={props["aria-label"] ?? "선택 목록 열기"}
              />
            </InputGroupButton>
          )}
          {showClear && <ComboboxClear disabled={disabled} />}
        </InputGroupAddon>
        {children}
      </InputGroup>
    </PopoverPrimitive.Anchor>
  )
}

function ComboboxContent({
  className,
  side = "bottom",
  sideOffset = 6,
  align = "start",
  children,
  "aria-label": ariaLabel = "선택 목록",
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  const { filteredItems } = useComboboxContext()

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="combobox-content"
        data-empty={filteredItems.length === 0 ? "" : undefined}
        side={side}
        sideOffset={sideOffset}
        align={align}
        aria-label={ariaLabel}
        className={cn(
          "group/combobox-content relative z-50 max-h-96 w-(--radix-popover-trigger-width) overflow-hidden rounded-md bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          className,
        )}
        {...props}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
}

function ComboboxSearchInput({
  className,
  children,
  ...props
}: React.ComponentProps<"input">) {
  const { inputValue, setInputValue } = useComboboxContext()

  return (
    <div className="border-b p-1">
      <InputGroup className={cn("w-full", className)}>
        <InputGroupAddon>
          <SearchIcon />
        </InputGroupAddon>
        <InputGroupInput
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value)
            props.onChange?.(event)
          }}
          {...props}
        />
        {children}
      </InputGroup>
    </div>
  )
}

function ComboboxList({
  className,
  children,
  "aria-label": ariaLabel = "선택 항목",
  ...props
}: Omit<React.ComponentProps<"div">, "children"> & {
  children?: React.ReactNode | ((item: any) => React.ReactNode)
}) {
  const { filteredItems } = useComboboxContext()

  return (
    <div
      data-slot="combobox-list"
      role="listbox"
      aria-label={ariaLabel}
      className={cn(
        "max-h-[min(calc(--spacing(96)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 overflow-y-auto p-1 data-empty:p-0",
        className,
      )}
      {...props}
    >
      {typeof children === "function"
        ? filteredItems.map((item) => children(item))
        : children}
    </div>
  )
}

function ComboboxItem<TItem>({
  className,
  children,
  value,
  onClick,
  ...props
}: Omit<React.ComponentProps<"div">, "value"> & {
  value: TItem
}) {
  const { isItemSelected, selectItem } = useComboboxContext()
  const selected = isItemSelected(value)

  return (
    <div
      data-slot="combobox-item"
      data-selected={selected ? "" : undefined}
      role="option"
      aria-selected={selected}
      className={cn(
        "relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      onClick={(event) => {
        selectItem(value)
        onClick?.(event)
      }}
      {...props}
    >
      {children}
      {selected ? (
        <span
          data-slot="combobox-item-indicator"
          className="pointer-events-none absolute right-2 flex size-4 items-center justify-center"
        >
          <CheckIcon className="pointer-events-none size-4 pointer-coarse:size-5" />
        </span>
      ) : null}
    </div>
  )
}

function ComboboxGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="combobox-group"
      className={cn(className)}
      {...props}
    />
  )
}

function ComboboxLabel({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="combobox-label"
      className={cn(
        "px-2 py-1.5 text-xs text-muted-foreground pointer-coarse:px-3 pointer-coarse:py-2 pointer-coarse:text-sm",
        className,
      )}
      {...props}
    />
  )
}

function ComboboxCollection({ ...props }: React.ComponentProps<"div">) {
  return <div data-slot="combobox-collection" {...props} />
}

function ComboboxEmpty({ className, ...props }: React.ComponentProps<"div">) {
  const { filteredItems } = useComboboxContext()

  return (
    <div
      data-slot="combobox-empty"
      hidden={filteredItems.length > 0}
      className={cn(
        "w-full justify-center py-2 text-center text-sm text-muted-foreground",
        className,
      )}
      {...props}
    />
  )
}

function ComboboxSeparator({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="combobox-separator"
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  )
}

function ComboboxChips({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="combobox-chips"
      className={cn(
        "flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent bg-clip-padding px-2.5 py-1.5 text-sm shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 has-aria-invalid:border-destructive has-aria-invalid:ring-[3px] has-aria-invalid:ring-destructive/20 has-data-[slot=combobox-chip]:px-1.5 dark:bg-input/30 dark:has-aria-invalid:border-destructive/50 dark:has-aria-invalid:ring-destructive/40",
        className,
      )}
      {...props}
    />
  )
}

function ComboboxChip({
  className,
  children,
  showRemove = true,
  ...props
}: React.ComponentProps<"div"> & {
  showRemove?: boolean
}) {
  return (
    <div
      data-slot="combobox-chip"
      className={cn(
        "flex h-[calc(--spacing(5.5))] w-fit items-center justify-center gap-1 rounded-sm bg-muted px-1.5 text-xs font-medium whitespace-nowrap text-foreground has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50 has-data-[slot=combobox-chip-remove]:pr-0",
        className,
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <Button
          aria-label={`${typeof children === "string" ? children : "선택 항목"} 제거`}
          variant="ghost"
          size="icon-xs"
          className="-ml-1 opacity-50 hover:opacity-100"
          data-slot="combobox-chip-remove"
        >
          <XIcon className="pointer-events-none" />
        </Button>
      )}
    </div>
  )
}

function ComboboxChipsInput({
  className,
  ...props
}: React.ComponentProps<"input">) {
  return (
    <input
      data-slot="combobox-chip-input"
      className={cn("min-w-16 flex-1 outline-none", className)}
      {...props}
    />
  )
}

function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null)
}

export {
  Combobox,
  ComboboxInput,
  ComboboxSearchInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxGroup,
  ComboboxLabel,
  ComboboxCollection,
  ComboboxEmpty,
  ComboboxSeparator,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
}
